import { KeyboardEventHandler } from 'react';
import { UseComboboxStateChange, useCombobox } from 'downshift';
import { observer } from 'mobx-react-lite';
import { css } from '@emotion/core';
import isPropValid from '@emotion/is-prop-valid';
import styled from '@emotion/styled';
import { Color } from '../../../../styles/Color';
import { FlightSearchSuggestionItem } from './FlightSearchSuggestionItem';
import { useFlightStores } from '../../shared/context/flightStoresContext';
import { IFlightSearchSuggestion } from '../domain/models/IFlightSearchSuggestion';
import { SearchInput } from '../../../../components/common/search-input/SearchInput';
import { Css, ITheme } from '../../../../ui';

const Dropdown = styled('ul', { shouldForwardProp: isPropValid })<
	{
		isOpen: boolean;
	},
	ITheme
>`
	position: absolute;
	top: 100%;
	left: 0;
	right: 0;
	background: #fff;
	list-style: none;
	padding: 0;
	margin: 0;
	max-height: 300px;
	overflow-y: auto;
	z-index: ${(props) => props.theme.zIndex?.dropdown};
	border-radius: 8px;
	background: ${Color.Neutral2};
	border: 1px solid ${Color.BorderCard};

	${({ isOpen }) => css`
		visibility: ${isOpen ? 'visible' : 'hidden'};
	`}
`;

const Option = styled.li<{}, ITheme>`
	padding: ${Css.pxToRem(16)};
	color: ${Color.Neutral6};

	strong {
		color: ${(props) => props.theme.color.onPrimary};
	}

	&[aria-selected='true'] {
		background: ${(props) => props.theme.color.primaryLight};
	}

	&:hover,
	&:focus {
		cursor: pointer;
		background: ${(props) => props.theme.color.primaryLight};
	}
`;

const Wrapper = styled.div`
	position: relative;
	width: 100%;
`;

function itemToString(item: IFlightSearchSuggestion | null) {
	return item?.stringValue || '';
}

interface IProps {
	value: string;
	onClear: () => void;
	onItemClick: (item: IFlightSearchSuggestion) => void;
	onKeyUp?: KeyboardEventHandler<HTMLInputElement>;
}

export const FlightSearchCombobox = observer(
	({ value, onItemClick, onClear, onKeyUp }: IProps) => {
		const { flightSuggestionStore } = useFlightStores();
		const { suggestions, setQuery } = flightSuggestionStore;

		const onInputValueChange = ({
			type,
			inputValue,
			selectedItem,
		}: UseComboboxStateChange<IFlightSearchSuggestion>) => {
			if (type === useCombobox.stateChangeTypes.ItemClick && selectedItem) {
				onItemClick(selectedItem);
			}

			if (inputValue != null) {
				setQuery(inputValue);
			}
		};

		const {
			isOpen,
			getComboboxProps,
			getMenuProps,
			getLabelProps,
			getInputProps,
			getItemProps,
			inputValue,
		} = useCombobox<IFlightSearchSuggestion>({
			id: 'flight-search-combobox',
			items: suggestions,
			itemToString,
			onInputValueChange,
		});

		return (
			<Wrapper {...getComboboxProps()}>
				<SearchInput
					{...getInputProps()}
					value={value}
					label="Flight number, Destination, Airline"
					labelId={getLabelProps().id}
					onClear={onClear}
					onKeyUp={onKeyUp}
				/>

				<Dropdown {...getMenuProps()} isOpen={!!(isOpen && value)}>
					{suggestions.map((item, index) => (
						<Option
							key={item.value}
							{...getItemProps({ item, index })}
							className="no-max-width"
						>
							<FlightSearchSuggestionItem
								query={inputValue}
								value={item.value}
							/>
						</Option>
					))}
				</Dropdown>
			</Wrapper>
		);
	},
);
